// LineItem/LineItemRow.jsx
import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  Tr,
  Td,
  IconButton,
  Flex,
  FormControl,
  Input,
  Box,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';

import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import InputField from 'components/ui/InputField';
import { formatAmount } from 'utils/util';
import { SupplyTypes } from 'utils/dropdownEnum';

// ✅ DebouncedInput: Shows typed value instantly, calls onChange after delay
const DebouncedInput = React.forwardRef(
  ({ value, onChange, delay = 300, ...props }, ref) => {
    const [localValue, setLocalValue] = useState(value ?? '');
    const timerRef = useRef(null);

    // Sync if parent value changes externally (e.g. row reset)
    useEffect(() => {
      setLocalValue(value ?? '');
    }, [value]);

    const handleChange = useCallback(
      (e) => {
        const newVal = e.target.value;
        setLocalValue(newVal); // instant UI update

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          onChange(e); // debounced parent update
        }, delay);
      },
      [onChange, delay]
    );

    // Flush on unmount to avoid lost updates
    useEffect(() => {
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, []);

    return <Input ref={ref} {...props} value={localValue} onChange={handleChange} />;
  }
);
DebouncedInput.displayName = 'DebouncedInput';

const LineItemRow = ({
  rowIndex,
  lineItem,
  lineItems,
  columnHeaders,
  itemData,
  screenData,
  uqcData,
  businessTypes,
  company,
  companySetting,
  lastDaybook,
  selectcompany,
  type,
  wareshow,
  lineitemview,
  lineDetailview,
  warehouseList,
  errors,
  refs,
  prerefs,
  billTermData,
  addLineItem,
  removeLineItem,
  handleOnChange,
  handleStockModalOnClick,
  handleModalOpen,
  setCurrentLineItemIdx,
  textColor,
  activeRow,
  setActiveRow,
  handleStockModal
}) => {
  const selectedItem = useMemo(() => {
    return itemData?.find((item) => item.id == lineItem?.itemId);
  }, [itemData, lineItem?.itemId]);
  const rowHeaders = columnHeaders;
  const itemIdRefs = useRef([]);

  const unit = lineItem?.unit;
  const unitList = uqcData
    ?.filter((uqc) =>
      [selectedItem?.uqcid1, selectedItem?.uqcid2, selectedItem?.uqcid3].includes(uqc.id)
    )
    ?.sort((a, b) => {
      const order = [selectedItem?.uqcid1, selectedItem?.uqcid2, selectedItem?.uqcid3];
      return order.indexOf(a.id) - order.indexOf(b.id);
    });

  const serialitem = selectedItem?.itemdetailtype !== 1;
  const showWarehouset =
    (type === 'purchase' && lineItem?.itemId && serialitem) ||
    (type === 'recive' && lineItem?.itemId && serialitem);

  const showWarehouse =
    selectcompany?.setting?.warehouseWishStock === true && lineitemview === true
      ? true
      : !!showWarehouset;

  const qty =
    unit === selectedItem?.uqc1Data?.id
      ? lineItem?.qty1
      : unit === selectedItem?.uqc2Data?.id
        ? lineItem?.qty2
        : unit === selectedItem?.uqc3Data?.id
          ? lineItem?.qty3
          : lineItem?.qty1;

  const handleFocus = (rowIndex) => {
    setActiveRow(rowIndex);
  };

  const SAFE_EXPR = /^[\d\s\+\-\*\/\.\(\)]+$/;
  const hasOperator = (val) => /[+\-*\/()]/.test(String(val));

  const evaluateExpression = (expr) => {
    const cleaned = String(expr).trim();
    if (!SAFE_EXPR.test(cleaned)) return null;
    try {
      const result = Function('"use strict"; return (' + cleaned + ')')();
      return typeof result === 'number' && isFinite(result) ? result : null;
    } catch {
      return null;
    }
  };

  return (
    <Tr bgColor={lineItem.isDeleted ? 'red.200' : ''}>
      {/* Index */}
      <Td textAlign="center" color={textColor}>
        {rowIndex + 1}
      </Td>

      {/* Action */}
      <Td>
        <IconButton
          onClick={() => removeLineItem(rowIndex, lineItem?.id)}
          colorScheme="red"
          size="sm"
          icon={<FaTrash />}
          disabled={lineItems.length === 1 || lineItem.isDeleted || lineItem?.isDisabled}
        />
      </Td>

      {/* Warehouse */}
      {(selectcompany?.setting?.warehouseWishStock === true && lineitemview === true) ||
        (wareshow && type === 'purchase' && lineDetailview === true) ||
        (wareshow && type === 'recive' && lineDetailview === true) ? (
        <Td>
          {showWarehouse ? (
            renderDynamicDropdown(
              'warehouseId',
              'Ware House',
              warehouseList,
              {
                value: lineItem?.warehouseId,
                ref: (el) => {
                  if (refs?.warehouseRef?.current) {
                    refs.warehouseRef.current[rowIndex] = el;
                  }
                },
                onChange: (value) =>
                  handleOnChange('lineItem', 'warehouseId', value, rowIndex),
                showCustomNoOptions: true,
                openfrom: 'wareHouse',
                disabled: !!lineItem?.isDeleted,
                onKeyDown: (e) => {
                  if (!e.shiftKey && (e.key === 'Enter' || e.key === 'Tab')) {
                    e.preventDefault();
                    if (
                      businessTypes?.businessType?.toUpperCase() === 'TEXTILE' &&
                      lastDaybook &&
                      (lastDaybook?.isMillIssue || lastDaybook?.isOtherJobWork)
                    ) {
                      refs?.purchaseDocumentRef?.current[rowIndex]?.focus();
                    } else if (
                      businessTypes?.businessType?.toUpperCase() === 'TEXTILE' &&
                      lastDaybook &&
                      lastDaybook?.isMillReceive &&
                      lastDaybook?.issuedaybookid !== null
                    ) {
                      refs?.issueDocRefs?.current[rowIndex]?.focus();
                    } else {
                      refs?.itemRefs?.current[rowIndex]?.focus();
                    }
                  }

                  if (e.shiftKey && e.key === 'Tab') {
                    e.preventDefault();
                    const rateRef = refs.rateRefs.current[rowIndex - 1]
                    if (rowIndex === 0) {
                      prerefs?.current?.focus();
                      return;
                    }
                    if (rateRef) {
                      rateRef.focus()
                    }
                  }
                },
              },
              errors,
              textColor,
              false,
              'Select Ware House'
            )
          ) : (
            <Box h="40px" />
          )}
        </Td>
      ) : null}

      {/* 👉 Item Screen */}
      {lastDaybook?.isEnableItemScreen ? (<Td>
        <Flex justifyContent="flex-start" alignItems="center" gap={2}>
          {renderDynamicDropdown(
            'screenItemId',
            'Item Screen',
            screenData,
            {
              errorName: `lineItem[${rowIndex}].screenItemId`,
              value: lineItem?.screenItemId,
              onChange: async (value) => {
                await handleOnChange('lineItem', 'screenItemId', value, rowIndex);
                setCurrentLineItemIdx?.(rowIndex);
                const selected = screenData?.find(i => i.id == value);

                if (selected?.itemId) {
                  await handleOnChange('lineItem', 'itemId', selected.itemId, rowIndex);
                  handleModalOpen(selected.itemId, rowIndex);
                } else {
                  handleModalOpen(value, rowIndex);
                }

                const cutValue = selected?.cut ?? selected?.defaultcut;
                const parsedCut = parseFloat(cutValue);

                if (cutValue !== undefined && cutValue !== null && cutValue !== '') {
                  await handleOnChange('lineItem', 'qty2', cutValue, rowIndex);
                }

                if (selected?.itemId && itemData?.find(i => i.id == selected.itemId)?.calculatemts) {
                  const qty1Val = parseFloat(lineItem?.qty1 ?? 0);
                  const qty2Val = !isNaN(parsedCut) ? parsedCut : 0;
                  const qty3Calculated = qty1Val * qty2Val;
                  if (!isNaN(qty3Calculated) && qty3Calculated > 0) {
                    await handleOnChange('lineItem', 'qty3', qty3Calculated, rowIndex);
                  }
                }
              },
              focusBorderColor: 'blue.500',
              _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
              ref: (el) => {
                if (refs?.itemRefs?.current) {
                  refs.itemRefs.current[rowIndex] = el;
                }
              },
              showCustomNoOptions: true,
              openfrom: 'Screen',
              nextRef: refs?.qty1Refs?.current[rowIndex],
              daybook: lastDaybook,
              hideStock: !!lastDaybook?.purchaseDaybookId,
              disabled: !!lineItem?.isDeleted || lineItem?.isDisabled,
              isClearable: false,
              onKeyDown: async (e) => { },
              handleStockModalOnClick: () => handleStockModalOnClick(rowIndex),
              rowIndex,
            },
            errors,
            textColor,
            false,
            'Select Item Screen'
          )}
        </Flex>
      </Td>) : null}

      {/* Item */}
      <Td>
        <Flex justifyContent="flex-start" alignItems="center" gap={2}>
          {renderDynamicDropdown(
            'itemId',
            'Item',
            itemData,
            {
              errorName: `lineItem[${rowIndex}].itemId`,
              value: lineItem?.itemId,
              onChange: async (value) => {
                await handleOnChange('lineItem', 'itemId', value, rowIndex);
                handleModalOpen(value, rowIndex);
                setCurrentLineItemIdx?.(rowIndex);
                const selected = itemData?.find(i => i.id == value);

                if (selected?.itemdetailtype === 0) {
                  refs?.qty1Refs?.current[rowIndex]?.focus();
                } else if (selected?.itemdetailtype === 1) {
                  if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst) {
                    refs?.gstRateRefs?.current[rowIndex]?.focus();
                  } else {
                    refs?.rateRefs?.current[rowIndex]?.focus();
                  }
                }
              },
              focusBorderColor: 'blue.500',
              _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
              ref: (el) => {
                if (refs?.itemRefs?.current) {
                  refs.itemRefs.current[rowIndex] = el;
                }
              },
              showCustomNoOptions: true,
              openfrom: 'item',
              nextRef: refs?.qty1Refs?.current[rowIndex],
              daybook: lastDaybook,
              hideStock: !!lastDaybook?.purchaseDaybookId,
              disabled: !!lineItem?.isDeleted || lineItem?.isDisabled || lineItem?.isDisItem,
              isClearable: false,
              onKeyDown: async (e) => {
                if (e.key === 'Enter' && lineItem?.itemId) {
                  e.preventDefault();
                  if (e.shiftKey) {
                    if (
                      (selectcompany?.setting?.warehouseWishStock === true && lineitemview === true) ||
                      (wareshow && type === 'purchase' && lineDetailview === true) ||
                      (wareshow && type === 'recive' && lineDetailview === true)
                    ) {
                      refs?.warehouseRef?.current[rowIndex]?.focus();
                    } else {
                      if (rowIndex === 0) {
                        prerefs?.current?.focus();
                      } else {
                        refs?.rateRefs?.current[rowIndex - 1]?.focus();
                      }
                    }
                  } else {
                    if (selectedItem?.itemdetailtype === 0) {
                      if (refs?.qty1Refs?.current[rowIndex] && !refs?.qty1Refs?.current[rowIndex]?.disabled) {
                        refs.qty1Refs.current[rowIndex].focus();
                        return;
                      }
                      if (refs?.rateRefs?.current[rowIndex]) {
                        refs.rateRefs.current[rowIndex].focus();
                        return;
                      }
                    }
                    if (selectedItem?.itemdetailtype === 1) {
                      if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst) {
                        refs?.gstRateRefs?.current[rowIndex]?.focus();
                        return;
                      }
                      if (refs?.rateRefs?.current[rowIndex]) {
                        refs.rateRefs.current[rowIndex].focus();
                        return;
                      }
                    }
                    if (!selectedItem?.id && lineItems.length > 1) {
                      await removeLineItem(rowIndex, null);
                      if (billTermData?.length > 0 && refs?.percentageRefs?.current && refs.percentageRefs.current[0]) {
                        refs.percentageRefs.current[0].focus();
                        return;
                      }
                      if (refs?.dueDaysRef?.current) {
                        refs.dueDaysRef.current.focus();
                        return;
                      }
                    }
                    if (refs?.qty1Refs?.current[rowIndex]) {
                      refs.qty1Refs.current[rowIndex].focus();
                      return;
                    }
                  }
                }

                if (e.key === 'Tab' && lineItem?.itemId) {
                  const isWarehouseShow =
                    (selectcompany?.setting?.warehouseWishStock === true && lineitemview === true) ||
                    (wareshow && type === 'purchase' && lineDetailview === true) ||
                    (wareshow && type === 'recive' && lineDetailview === true);
                  const warehouseRef = refs?.warehouseRef?.current[rowIndex];
                  const rateRef = refs?.rateRefs?.current[rowIndex - 1];
                  e.preventDefault();
                  if (e.shiftKey) {
                    if (rowIndex === 0) { prerefs?.current?.focus(); return; }
                    if (isWarehouseShow && warehouseRef) { warehouseRef.focus(); return; }
                    if (rateRef) { rateRef.focus(); return; }
                  } else {
                    if (selectedItem?.itemdetailtype === 1) {
                      refs?.rateRefs?.current[rowIndex]?.focus();
                    } else {
                      refs?.qty1Refs?.current[rowIndex]?.focus();
                    }
                  }
                }

                if (e.key === 'Enter' || e.key === 'Tab') {
                  const prevLineItem = await lineItems[rowIndex - 1];
                  if (!lineItem?.itemId && lineItems.length > 1 && prevLineItem?.itemId) {
                    await removeLineItem(rowIndex, null);
                    if (billTermData?.length > 0 && refs?.percentageRefs?.current && refs.percentageRefs.current[0]) {
                      refs.percentageRefs.current[0].focus();
                      return;
                    }
                    if (refs?.dueDaysRef?.current) {
                      refs.dueDaysRef.current.focus();
                      return;
                    }
                  }
                }
              },
              handleStockModalOnClick: () => handleStockModalOnClick(rowIndex),
              rowIndex,
            },
            errors,
            textColor,
            false,
            'Select Item'
          )}

          {lineItem?.itemId &&
            lineItem?.itemId > 0 &&
            (selectedItem?.itemdetailtype === 1 ||
              (lastDaybook?.getLineDetail && businessTypes?.businessType?.toUpperCase() === 'TEXTILE')) && (
              <IconButton
                onClick={() => {
                  setCurrentLineItemIdx?.(rowIndex);
                  if (lineItem?.issueLineId && lineItem?.issueLineId > 0 && lastDaybook?.isMillReceive) {
                    handleStockModal(lineItem?.itemId, rowIndex);
                  } else if (lastDaybook?.getLineDetail && lineItem?.pcliId && lastDaybook?.challanFieldRegion === 0 && lastDaybook?.challandaybookid) {
                    handleStockModal(lineItem?.itemId, rowIndex);
                  } else if (lastDaybook?.getLineDetail && lineItem?.scliId) {
                    handleStockModal(lineItem?.itemId, rowIndex);
                  } else if (lastDaybook?.isMillIssue && (lineItem?.pliId || lineItem?.pcliId)) {
                    handleStockModal(lineItem?.itemId, rowIndex);
                  } else {
                    handleStockModalOnClick(rowIndex);
                  }
                }}
                colorScheme="green"
                size="sm"
                icon={<FaPlus />}
                disabled={!!lineItem?.isDeleted || lineItem?.soliId}
              />
            )}
        </Flex>
      </Td>

      {/* Type of Supply */}
      {lastDaybook?.isAskTypeOfSupply ? (
        <Td>
          {renderDynamicDropdown(
            'typeOfSupply',
            'Type of Supply',
            SupplyTypes,
            {
              value: lineItem?.typeOfSupply ?? 1,
              onChange: (value) => handleOnChange('lineItem', 'typeOfSupply', value, rowIndex),
              focusBorderColor: 'blue.500',
              _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
              disabled: !!lineItem?.isDeleted,
            },
            errors,
            textColor,
            true,
            'Select Item'
          )}
        </Td>
      ) : null}

      {/* Unit */}
      {companySetting?.maxUOM > 1 ? (
        <Td>
          {renderDynamicDropdown(
            'unit',
            'Unit',
            unitList ?? [],
            {
              value: unit,
              onChange: (value) => handleOnChange('lineItem', 'unit', value, rowIndex),
              focusBorderColor: 'blue.500',
              _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
              disabled: !!lineItem?.isDeleted,
            },
            errors,
            textColor,
            true,
            'Select Unit'
          )}
        </Td>
      ) : null}

      {/* 👉 Lot Number */}
      {businessTypes?.businessType?.toUpperCase() === 'TEXTILE' && (lastDaybook?.isMillReceive || lastDaybook?.isOtherJobWork) ? (
        <Td>
          <FormControl>
            <Input
              name="lotNumber"
              value={lineItem?.lotNumber}
              onChange={(e) => handleOnChange('lineItem', 'lotNumber', e.target.value, rowIndex)}
              onFocus={() => handleFocus(rowIndex)}
              size="sm"
              type="number"
              placeholder="Lot Number"
              disabled={!!lineItem?.isDeleted}
              onWheel={(e) => e.target.blur()}
            />
          </FormControl>
        </Td>
      ) : null}

      {/* Qty1 */}
      {companySetting?.maxUOM > 0 && companySetting?.maxUOM <= 3 ? (
        <Td>
          <FormControl>
            <InputField
              ref={(el) => {
                if (refs?.qty1Refs?.current) {
                  refs.qty1Refs.current[rowIndex] = el;
                }
              }}
              name="qty1"
              errorName={`lineItem[${rowIndex}].qty1`}
              placeholder={selectedItem?.uqc1Data ? selectedItem.uqc1Data.name : 'Qty 1'}
              value={lineItem?.qty1 || ''}
              onChange={(e) => {
                handleOnChange('lineItem', 'qty1', e.target.value, rowIndex);
                if (selectedItem?.calculatemts) {
                  const qty1Val = parseFloat(e.target.value ?? 0);
                  const qty2Val = parseFloat(lineItem?.qty2 ?? 0);
                  const qty3Calculated = qty1Val * qty2Val;
                  if (!isNaN(qty3Calculated)) {
                    handleOnChange('lineItem', 'qty3', qty3Calculated, rowIndex);
                  }
                }
              }}
              onFocus={() => handleFocus(rowIndex)}
              disabled={selectedItem?.itemdetailtype === 1 || lineItem?.isDeleted || lastDaybook?.getLineDetail || lineItem?.scliId || lineItem?.pcliId}
              size="sm"
              type="number"
              errors={errors}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (businessTypes?.businessType?.toUpperCase() !== 'MOBILE') {
                    if ((companySetting?.maxUOM >= 2 && companySetting?.maxUOM <= 3)) {
                      refs.qty2Ref.current[rowIndex].focus();
                      return;
                    } else if (companySetting?.maxUOM === 3 && !(!!selectedItem?.calculatemts)) {
                      refs.qty3Ref.current[rowIndex].focus();
                      return;
                    }
                  }
                  if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst) {
                    refs.gstRateRefs.current[rowIndex].focus();
                    return;
                  }
                  refs.rateRefs.current[rowIndex].focus();
                  return;
                }
                if (e.key === 'Tab') {
                  e.preventDefault();
                  if (e.shiftKey) {
                    if (refs?.itemRefs?.current[rowIndex]) {
                      refs.itemRefs.current[rowIndex].focus();
                      return;
                    }
                  }
                  if (businessTypes?.businessType?.toUpperCase() !== 'MOBILE') {
                    if (refs?.qty2Ref?.current[rowIndex] && !refs?.qty2Refs?.current[rowIndex]?.disabled) {
                      refs.qty2Ref.current[rowIndex].focus();
                      return;
                    } else if (refs?.qty3Ref?.current[rowIndex] && !refs?.qty3Refs?.current[rowIndex]?.disabled) {
                      refs.qty3Ref.current[rowIndex].focus();
                      return;
                    }
                  }
                  if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst) {
                    refs.gstRateRefs.current[rowIndex].focus();
                    return;
                  }
                  refs.rateRefs.current[rowIndex].focus();
                  return;
                }
              }}
              onWheel={(e) => e.target.blur()}
            />
          </FormControl>
        </Td>
      ) : null}

      {/* Qty2 */}
      {companySetting?.maxUOM >= 2 && companySetting?.maxUOM <= 3 ? (
        <Td>
          <FormControl>
            <InputField
              ref={(el) => {
                if (refs?.qty2Ref?.current) {
                  refs.qty2Ref.current[rowIndex] = el;
                }
              }}
              name="qty2"
              placeholder={selectedItem?.uqc2Data ? selectedItem.uqc2Data.name : 'Qty 2'}
              value={lineItem?.qty2 || ''}
              onChange={(e) => {
                handleOnChange('lineItem', 'qty2', e.target.value, rowIndex);
                if (selectedItem?.calculatemts) {
                  const qty1Val = parseFloat(lineItem?.qty1 ?? 0);
                  const qty2Val = parseFloat(e.target.value ?? 0);
                  const qty3Calculated = qty1Val * qty2Val;
                  if (!isNaN(qty3Calculated)) {
                    handleOnChange('lineItem', 'qty3', qty3Calculated, rowIndex);
                  }
                }
              }}
              onFocus={() => handleFocus(rowIndex)}
              disabled={selectedItem?.itemdetailtype === 1 || lineItem?.isDeleted || lastDaybook?.getLineDetail || lineItem?.scliId || lineItem?.pcliId}
              size="sm"
              type="number"
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (businessTypes?.businessType?.toUpperCase() !== 'MOBILE') {
                    if (companySetting?.maxUOM === 3 && !(!!selectedItem?.calculatemts)) {
                      refs.qty3Ref.current[rowIndex].focus();
                      return;
                    }
                  }
                  if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst) {
                    refs.gstRateRefs.current[rowIndex].focus();
                    return;
                  }
                  refs.rateRefs.current[rowIndex].focus();
                  return;
                }
              }}
              onWheel={(e) => e.target.blur()}
            />
          </FormControl>
        </Td>
      ) : null}

      {/* Qty3 */}
      {companySetting?.maxUOM === 3 ? (
        <Td>
          <FormControl>
            <InputField
              ref={(el) => {
                if (refs?.qty3Ref?.current) {
                  refs.qty3Ref.current[rowIndex] = el;
                }
              }}
              name="qty3"
              placeholder={selectedItem?.uqc3Data ? selectedItem.uqc3Data.name : 'Qty 3'}
              value={lineItem?.qty3 || ''}
              onChange={(e) => handleOnChange('lineItem', 'qty3', e.target.value, rowIndex)}
              onFocus={() => handleFocus(rowIndex)}
              disabled={selectedItem?.itemdetailtype === 1 || lineItem?.isDeleted || lastDaybook?.getLineDetail || !!selectedItem?.calculatemts || lineItem?.scliId || lineItem?.pcliId}
              size="sm"
              type="number"
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst) {
                    refs.gstRateRefs.current[rowIndex].focus();
                    return;
                  }
                  refs.rateRefs.current[rowIndex].focus();
                  return;
                }
              }}
              onWheel={(e) => e.target.blur()}
            />
          </FormControl>
        </Td>
      ) : null}

      {/* GST Rate (Line) */}
      {lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst ? (
        <Td>
          <FormControl>
            <InputField
              ref={(el) => {
                if (refs?.gstRateRefs?.current) {
                  refs.gstRateRefs.current[rowIndex] = el;
                }
              }}
              name="gstWithRate"
              id={`gstWithRate-${rowIndex}`}
              errorName={`lineItem[${rowIndex}].gstWithRate`}
              value={lineItem?.gstWithRate || ''}
              onChange={(e) => handleOnChange('lineItem', 'gstWithRate', e.target.value, rowIndex)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  refs?.rateRefs?.current[rowIndex]?.focus();
                }
                if (e.key === 'Tab') {
                  e.preventDefault();
                  if (e.shiftKey) {
                    if (refs?.qty1Refs?.current[rowIndex] && !refs?.qty1Refs?.current[rowIndex]?.disabled) {
                      refs.qty1Refs.current[rowIndex].focus();
                    } else {
                      refs?.itemRefs?.current[rowIndex]?.focus();
                    }
                  } else {
                    refs?.rateRefs?.current[rowIndex]?.focus();
                  }
                }
              }}
              onFocus={() => handleFocus(rowIndex, rowHeaders.length + 2, 'GST Rate')}
              onBlur={(e) => {
                const value = e.target.value;
                if (value !== '') {
                  handleOnChange('lineItem', 'gstWithRate', Number(value).toFixed(2), rowIndex);
                }
              }}
              size="sm"
              step="0.01"
              type="number"
              onWheel={(e) => e.target.blur()}
            />
          </FormControl>
        </Td>
      ) : null}

      {/* Rate */}
      {/* <Td>
        <FormControl>
          <InputField
            ref={(el) => {
              if (refs?.rateRefs?.current) {
                refs.rateRefs.current[rowIndex] = el;
              }
            }}
            name="itemRate"
            id={`itemRate-${rowIndex}`}
            errorName={`lineItem[${rowIndex}].itemRate`}
            value={lineItem?.itemRate || ''}
            onChange={(e) => handleOnChange('lineItem', 'itemRate', e.target.value, rowIndex)}
            onFocus={() => handleFocus(rowIndex, rowHeaders.length + 2, 'Rate')}
            onKeyDown={async (e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }
              if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Tab' && !e.shiftKey)) {
                e.preventDefault();
                if (lineItems[rowIndex]?.itemId && lineItems[rowIndex]?.itemId > 0) {
                  const amountRef = refs?.subamountRefs?.current[rowIndex];
                  const gstTaxableRef = refs?.gstTaxableRefs?.current?.[rowIndex];
                  const lineItemNotesRef = refs?.lineItemNotesRef?.current[rowIndex];
                  const lineItemRemarkRef = refs?.lineItemRemarkRef?.current[rowIndex];
                  const itemRef = refs?.itemRefs?.current[rowIndex + 1];
                  const amountFocusById = document.getElementById(`amount-${rowIndex}`)?.disabled;

                  if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst && !gstTaxableRef?.disabled) {
                    gstTaxableRef.focus();
                    return;
                  }
                  if (lastDaybook?.isAllowNotes && !!lineItemNotesRef) {
                    lineItemNotesRef.focus();
                    return;
                  }
                  if (lastDaybook?.isAllowRemark && !!lineItemRemarkRef) {
                    lineItemRemarkRef.focus();
                    return;
                  }
                  if (!!amountRef && !amountFocusById) {
                    amountRef.focus();
                    return;
                  }
                  if (!itemRef) {
                    await addLineItem();
                    refs?.itemRefs?.current[rowIndex + 1]?.focus();
                    return;
                  } else {
                    itemRef.focus();
                    return;
                  }
                } else {
                  if (lineItems.length > 1) {
                    await removeLineItem(rowIndex, null);
                    if (billTermData?.length > 0 && refs?.percentageRefs?.current && refs.percentageRefs.current[0]) {
                      refs.percentageRefs.current[0].focus();
                    } else {
                      refs?.dueDaysRef?.current?.focus();
                    }
                  }
                  if (lineItems.length === 1) {
                    await addLineItem();
                    refs?.itemRefs?.current[rowIndex + 1]?.focus();
                  }
                }
              }
            }}
            size="sm"
            type="number"
            errors={errors}
            onWheel={(e) => e.target.blur()}
            disabled={!!lineItem?.isDeleted || lineItem?.isDisItemRate}
          />
        </FormControl>
      </Td> */}

      {/* Rate */}
      <Td>
        <FormControl>
          <InputField
            ref={(el) => {
              if (refs?.rateRefs?.current) {
                refs.rateRefs.current[rowIndex] = el;
              }
            }}
            name="itemRate"
            id={`itemRate-${rowIndex}`}
            errorName={`lineItem[${rowIndex}].itemRate`}
            value={lineItem?.itemRate || ''}

            // ✅ Change 1: type="text" so browser accepts "100*2"
            // inputMode keeps numeric keyboard on mobile
            type="text"
            inputMode="decimal"

            onChange={(e) => {
              const val = e.target.value;
              // ✅ If plain number → update immediately (existing behavior)
              // ✅ If expression → just store the raw string, wait for Enter/=
              if (!hasOperator(val)) {
                handleOnChange('lineItem', 'itemRate', val, rowIndex);
              } else {
                // Update only local display — don't trigger recalculation mid-typing
                handleOnChange('lineItem', 'itemRate', val, rowIndex);
              }
            }}

            onFocus={() => handleFocus(rowIndex, rowHeaders.length + 2, 'Rate')}

            // ✅ Change 2: Evaluate on blur (user clicks away)
            onBlur={(e) => {
              const raw = String(lineItem?.itemRate || '').trim();
              if (hasOperator(raw)) {
                const result = evaluateExpression(raw);
                if (result !== null) {
                  handleOnChange('lineItem', 'itemRate', parseFloat(result.toFixed(4)), rowIndex);
                }
              }
            }}

            onKeyDown={async (e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }

              // ✅ Change 3: Evaluate expression on Enter or = key
              if (e.key === '=') {
                e.preventDefault();
                const raw = String(lineItem?.itemRate || '').trim();
                if (hasOperator(raw)) {
                  const result = evaluateExpression(raw);
                  if (result !== null) {
                    handleOnChange('lineItem', 'itemRate', parseFloat(result.toFixed(4)), rowIndex);
                  }
                }
                return; // Don't move focus, let user see result
              }

              if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Tab' && !e.shiftKey)) {
                e.preventDefault();

                // ✅ If expression pending, evaluate first then move focus
                const raw = String(lineItem?.itemRate || '').trim();
                if (hasOperator(raw)) {
                  const result = evaluateExpression(raw);
                  if (result !== null) {
                    await handleOnChange('lineItem', 'itemRate', parseFloat(result.toFixed(4)), rowIndex);
                  }
                }

                // ---- your existing Enter/Tab navigation logic below (unchanged) ----
                if (lineItems[rowIndex]?.itemId && lineItems[rowIndex]?.itemId > 0) {
                  const amountRef = refs?.subamountRefs?.current[rowIndex];
                  const gstTaxableRef = refs?.gstTaxableRefs?.current?.[rowIndex];
                  const lineItemNotesRef = refs?.lineItemNotesRef?.current[rowIndex];
                  const lineItemRemarkRef = refs?.lineItemRemarkRef?.current[rowIndex];
                  const itemRef = refs?.itemRefs?.current[rowIndex + 1];
                  const amountFocusById = document.getElementById(`amount-${rowIndex}`)?.disabled;

                  if (lastDaybook?.isAllowGstRate && companySetting?.applyGST && lastDaybook?.applyGst && !gstTaxableRef?.disabled) {
                    gstTaxableRef.focus();
                    return;
                  }
                  if (lastDaybook?.isAllowNotes && !!lineItemNotesRef) {
                    lineItemNotesRef.focus();
                    return;
                  }
                  if (lastDaybook?.isAllowRemark && !!lineItemRemarkRef) {
                    lineItemRemarkRef.focus();
                    return;
                  }
                  if (!!amountRef && !amountFocusById) {
                    amountRef.focus();
                    return;
                  }
                  if (!itemRef) {
                    await addLineItem();
                    refs?.itemRefs?.current[rowIndex + 1]?.focus();
                    return;
                  } else {
                    itemRef.focus();
                    return;
                  }
                } else {
                  if (lineItems.length > 1) {
                    await removeLineItem(rowIndex, null);
                    if (billTermData?.length > 0 && refs?.percentageRefs?.current && refs.percentageRefs.current[0]) {
                      refs.percentageRefs.current[0].focus();
                    } else {
                      refs?.dueDaysRef?.current?.focus();
                    }
                  }
                  if (lineItems.length === 1) {
                    await addLineItem();
                    refs?.itemRefs?.current[rowIndex + 1]?.focus();
                  }
                }
              }
            }}

            size="sm"
            errors={errors}
            onWheel={(e) => e.target.blur()}
            disabled={!!lineItem?.isDeleted}
          />
        </FormControl>
      </Td>

      {/* Amount */}
      {lastDaybook?.requireDiscountLineItem > 0 ? (
        <Td>
          <FormControl>
            <InputField
              ref={(el) => {
                if (refs?.subamountRefs?.current) {
                  refs.subamountRefs.current[rowIndex] = el;
                }
              }}
              id={`amount-${rowIndex}`}
              name="amount"
              value={lineItem?.amount}
              onChange={(e) => handleOnChange('lineItem', 'amount', e.target.value, rowIndex)}
              onFocus={() => handleFocus(rowIndex, rowHeaders.length + 2, 'Amount')}
              size="sm"
              type="number"
              disabled={
                qty >= 1 && lastDaybook?.allowEditLIAmount && !companySetting?.applyGST && !lastDaybook?.applyGst
                  ? false
                  : true
              }
            />
          </FormControl>
        </Td>
      ) : null}

      {/* Discounts */}
      {lastDaybook?.requireDiscountLineItem > 0 &&
        new Array(lastDaybook.requireDiscountLineItem).fill('').map((_, index) => (
          <React.Fragment key={index}>
            <Td>
              <FormControl>
                <Input
                  name={`addLess${index + 1}Percent`}
                  placeholder={`Disc ${index + 1} (%)`}
                  value={lineItem[`addLess${index + 1}Percent`] || ''}
                  onChange={(e) => handleOnChange('lineItem', `addLess${index + 1}Percent`, e.target.value, rowIndex)}
                  size="sm"
                  type="number"
                  disabled={!!lineItem?.isDeleted}
                />
              </FormControl>
            </Td>
            <Td isNumeric>
              <FormControl>
                <Input
                  name={`addLess${index + 1}Amount`}
                  placeholder={`Disc ${index + 1} (%)`}
                  value={formatAmount(lineItem[`addLess${index + 1}Amount`] || '')}
                  onChange={(e) => handleOnChange('lineItem', `addLess${index + 1}Amount`, e.target.value, rowIndex)}
                  size="sm"
                  type="number"
                  disabled
                />
              </FormControl>
            </Td>
          </React.Fragment>
        ))}

      {/* Net Amount */}
      <Td isNumeric>
        <FormControl>
          <Input
            name="netAmount"
            placeholder="Net Amount"
            value={formatAmount(lineItem?.netAmount || '')}
            onChange={(e) => handleOnChange('lineItem', 'netAmount', e.target.value, rowIndex)}
            size="sm"
            type="number"
            disabled
          />
        </FormControl>
      </Td>

      {/* GST Block */}
      {company?.setting?.applyGST && lastDaybook?.applyGst ? (
        <>
          <Td>
            <FormControl>
              <Input
                name="gst"
                value={lineItem?.gst || 0}
                onChange={(e) => handleOnChange('lineItem', 'gst', e.target.value, rowIndex)}
                onFocus={() => handleFocus(rowIndex)}
                size="sm"
                type="number"
                placeholder="GST Rate"
                disabled
              />
            </FormControl>
          </Td>

          <Td>
            <FormControl>
              <InputField
                ref={(el) => {
                  if (refs?.gstTaxableRefs?.current) {
                    refs.gstTaxableRefs.current[rowIndex] = el;
                  }
                }}
                id={`taxableAmount-${rowIndex}`}
                name="taxableAmount"
                value={lineItem?.taxableAmount || 0}
                onChange={(e) => handleOnChange('lineItem', 'taxableAmount', e.target.value, rowIndex)}
                onKeyDown={async (e) => {
                  e.preventDefault();
                  if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Tab' && !e.shiftKey)) {
                    if (lastDaybook?.isAllowNotes) {
                      if (refs?.lineItemNotesRef?.current[rowIndex]) {
                        refs.lineItemNotesRef.current[rowIndex].focus();
                        return;
                      }
                    } else if (lastDaybook?.isAllowRemark) {
                      if (refs?.lineItemRemarkRef?.current[rowIndex]) {
                        refs.lineItemRemarkRef.current[rowIndex].focus();
                        return;
                      }
                    }
                    if (lineItems[rowIndex]?.itemId && lineItems[rowIndex]?.itemId > 0) {
                      if (lineItems.length >= 1) {
                        await addLineItem();
                        refs?.itemRefs?.current[rowIndex + 1]?.focus();
                      }
                    } else {
                      if (lineItems.length > 1) {
                        await removeLineItem(rowIndex, null);
                        if (refs?.percentageRefs?.current && refs.percentageRefs.current[0]) {
                          refs.percentageRefs.current[0].focus();
                        } else {
                          refs?.dueDaysRef?.current?.focus();
                        }
                      }
                    }
                  } else if (e.key === 'Tab' && e.shiftKey) {
                    if (refs?.subamountRefs?.current[rowIndex] && !refs.subamountRefs.current[rowIndex]?.disabled) {
                      refs.subamountRefs.current[rowIndex].focus();
                    } else {
                      refs?.rateRefs?.current[rowIndex]?.focus();
                    }
                  }
                }}
                onFocus={() => handleFocus(rowIndex)}
                size="sm"
                type="number"
                placeholder="Taxable Amount"
                disabled={
                  qty >= 1 && lastDaybook?.allowEditLIAmount && companySetting?.applyGST && lastDaybook?.applyGst
                    ? false
                    : true
                }
              />
            </FormControl>
          </Td>

          <Td>
            <FormControl>
              <Input
                name="netGst"
                value={lineItem?.netGst?.toFixed(2) || 0}
                onChange={(e) => handleOnChange('lineItem', 'netGst', e.target.value, rowIndex)}
                onFocus={() => handleFocus(rowIndex)}
                size="sm"
                type="number"
                placeholder="Net GST"
                disabled
              />
            </FormControl>
          </Td>

          <Td>
            <FormControl>
              <Input
                name="gstAmount"
                value={lineItem?.gstAmount?.toFixed(2) || 0}
                onChange={(e) => handleOnChange('lineItem', 'gstAmount', e.target.value, rowIndex)}
                onFocus={() => handleFocus(rowIndex)}
                size="sm"
                type="number"
                placeholder="AMT + GST"
                disabled
              />
            </FormControl>
          </Td>
        </>
      ) : null}

      {/* ✅ Notes — uses DebouncedInput for instant typing, debounced state update */}
      {lastDaybook?.isAllowNotes ? (
        <Td>
          <FormControl>
            <DebouncedInput
              ref={(el) => {
                if (refs?.lineItemNotesRef?.current) {
                  refs.lineItemNotesRef.current[rowIndex] = el;
                }
              }}
              name="notes"
              value={lineItem?.notes || ''}
              onChange={(e) => handleOnChange('lineItem', 'notes', e.target.value, rowIndex)}
              onFocus={() => handleFocus(rowIndex)}
              size="sm"
              type="text"
              placeholder="Notes"
              onKeyDown={async (e) => {
                if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                  e.preventDefault();
                  if (lineItems[rowIndex]?.itemId && lineItems[rowIndex]?.itemId > 0) {
                    if (lastDaybook?.isAllowRemark) {
                      if (refs?.lineItemRemarkRef?.current[rowIndex]) {
                        refs.lineItemRemarkRef.current[rowIndex].focus();
                        return;
                      }
                    }
                    if (!refs?.itemRefs?.current[rowIndex + 1]) {
                      await addLineItem();
                      refs?.itemRefs?.current[rowIndex + 1]?.focus();
                    } else {
                      refs.itemRefs.current[rowIndex + 1].focus();
                    }
                  } else {
                    if (lineItems.length > 1) {
                      await removeLineItem(rowIndex, null);
                      if (refs?.percentageRefs?.current && refs.percentageRefs.current[0]) {
                        refs.percentageRefs.current[0].focus();
                      } else {
                        refs?.dueDaysRef?.current?.focus();
                      }
                    }
                    if (lineItems.length === 1) {
                      await addLineItem();
                      refs?.itemRefs?.current[rowIndex + 1]?.focus();
                    }
                  }
                }
              }}
              disabled={!!lineItem?.isDeleted}
            />
          </FormControl>
        </Td>
      ) : null}

      {/* ✅ Remarks — uses DebouncedInput for instant typing, debounced state update */}
      {lastDaybook?.isAllowRemark ? (
        <Td>
          <FormControl>
            <DebouncedInput
              ref={(el) => {
                if (refs?.lineItemRemarkRef?.current) {
                  refs.lineItemRemarkRef.current[rowIndex] = el;
                }
              }}
              name="remarks"
              value={lineItem?.remarks || ''}
              onChange={(e) => handleOnChange('lineItem', 'remarks', e.target.value, rowIndex)}
              onFocus={() => handleFocus(rowIndex)}
              size="sm"
              type="text"
              placeholder="Remarks"
              onKeyDown={async (e) => {
                if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                  e.preventDefault();
                  if (lineItems[rowIndex]?.itemId && lineItems[rowIndex]?.itemId > 0) {
                    if (!refs?.itemRefs?.current[rowIndex + 1]) {
                      await addLineItem();
                      refs?.itemRefs?.current[rowIndex + 1]?.focus();
                    } else {
                      refs.itemRefs.current[rowIndex + 1].focus();
                    }
                  } else {
                    if (lineItems.length > 1) {
                      await removeLineItem(rowIndex, null);
                      if (refs?.percentageRefs?.current && refs.percentageRefs.current[0]) {
                        refs.percentageRefs.current[0].focus();
                      } else {
                        refs?.dueDaysRef?.current?.focus();
                      }
                    }
                    if (lineItems.length === 1) {
                      await addLineItem();
                      refs?.itemRefs?.current[rowIndex + 1]?.focus();
                    }
                  }
                }
              }}
              disabled={!!lineItem?.isDeleted}
            />
          </FormControl>
        </Td>
      ) : null}
    </Tr>
  );
};

export default LineItemRow;